/**
 * Default widget style overrides — mirror the admin app's lightTheme and
 * darkTheme palettes verbatim (see api-agents.app/src/theme/colors.ts).
 *
 *   widget role               admin theme key
 *   ────────────────────────  ──────────────────────────────────
 *   primary                   brand.electricBlue
 *   secondary                 border.default
 *   page bg                   bg.primary
 *   page text                 text.primary
 *   sidebar bg                sidebar.bg
 *   sidebar text              sidebar.linkColor
 *   input bg / text           input.bg / input.text
 *   message user              brand.electricBlue / white
 *   chart palette             components/charts/chartTheme.ts (same hex set)
 */
export const DEFAULT_STYLE_OVERRIDES = {
  // Primary — brand Electric Blue
  primaryColorLight: "#3B82F6",
  primaryColorDark: "#3B82F6",

  // Send button hover — dedicated per-theme hover colour for the widget's send
  // button (default mirrors the widget preset's #1d4ed8 so existing widgets are
  // unchanged until an admin overrides it).
  sendButtonHoverLight: "#1d4ed8",
  sendButtonHoverDark: "#1d4ed8",

  // Secondary — admin bg.secondary (the surface used for selected list items
  // in the admin app, e.g. ServerSelector / WidgetSelector / Step7 review)
  secondaryColorLight: "#F5F5F5",
  secondaryColorDark: "#141417",

  // Page — admin bg.primary / text.primary
  pageBgLight: "#FFFFFF",
  pageBgDark: "#141417",
  pageTextLight: "#1D1D1F",
  pageTextDark: "#FFFFFF",

  // Sidebar — admin sidebar.bg + page text-primary (admin's muted
  // sidebar.linkColor was nearly invisible in the widget context).
  sidebarBgLight: "#FFFFFF",
  sidebarBgDark: "#141417",
  sidebarTextLight: "#1D1D1F",
  sidebarTextDark: "#FFFFFF",

  // Input — admin input.bg / input.text
  inputBgLight: "#E3E3E7",
  inputBgDark: "#3B3B3C",
  inputTextLight: "#141417",
  inputTextDark: "#FFFFFF",

  // User message bubble — brand Electric Blue
  messageUserBgLight: "#3B82F6",
  messageUserBgDark: "#3B82F6",
  messageUserTextLight: "#FFFFFF",
  messageUserTextDark: "#FFFFFF",

  // Assistant message — admin text.primary on transparent surface
  messageAssistantTextLight: "#1D1D1F",
  messageAssistantTextDark: "#FFFFFF",

  // Chart palette — brand-aligned pastels, no yellow tones
  // (mirrors api-agents.widget/src/index.css --widget-chart-color-* defaults)
  chartColor1: "#A8D4E0", // sky blue
  chartColor2: "#7FB3C2", // deep sky
  chartColor3: "#A8C498", // sage
  chartColor4: "#B8B4E8", // lavender
  chartColor5: "#E8A8A0", // dusty rose
  chartColor6: "#80C4B8", // muted teal
  chartColor7: "#9396C2", // slate purple
  chartColor8: "#98B4D8", // dust blue
  chartColor9: "#B8D8A0", // moss
  chartColor10: "#D4A8C4", // mauve

  // Geometry — admin radii.tile
  borderRadius: "12px",
  borderWidth: "0px",
  borderStyle: "solid",
  borderColorLight: "transparent",
  borderColorDark: "transparent",

  // Outer iframe corner radius — rounds the embedded iframe element itself on
  // the host page (not the interior surfaces). Empty by default so it is only
  // emitted when the admin sets it, preserving the loader's per-display-type
  // hardcoded defaults for existing widgets.
  iframeCornerRadius: "",
};

export const STYLE_OVERRIDE_KEY_MAP: Record<
  string,
  keyof typeof DEFAULT_STYLE_OVERRIDES
> = {
  "--widget-primary-color-light": "primaryColorLight",
  "--widget-primary-color-dark": "primaryColorDark",
  "--widget-send-button-hover-light": "sendButtonHoverLight",
  "--widget-send-button-hover-dark": "sendButtonHoverDark",
  "--widget-secondary-color-light": "secondaryColorLight",
  "--widget-secondary-color-dark": "secondaryColorDark",
  "--widget-page-bg-light": "pageBgLight",
  "--widget-page-bg-dark": "pageBgDark",
  "--widget-page-text-light": "pageTextLight",
  "--widget-page-text-dark": "pageTextDark",
  "--widget-sidebar-bg-light": "sidebarBgLight",
  "--widget-sidebar-bg-dark": "sidebarBgDark",
  "--widget-sidebar-text-light": "sidebarTextLight",
  "--widget-sidebar-text-dark": "sidebarTextDark",
  "--widget-input-bg-light": "inputBgLight",
  "--widget-input-bg-dark": "inputBgDark",
  "--widget-input-text-light": "inputTextLight",
  "--widget-input-text-dark": "inputTextDark",
  "--widget-message-user-bg-light": "messageUserBgLight",
  "--widget-message-user-bg-dark": "messageUserBgDark",
  "--widget-message-user-text-light": "messageUserTextLight",
  "--widget-message-user-text-dark": "messageUserTextDark",
  "--widget-message-assistant-text-light": "messageAssistantTextLight",
  "--widget-message-assistant-text-dark": "messageAssistantTextDark",
  "--widget-chart-color-1": "chartColor1",
  "--widget-chart-color-2": "chartColor2",
  "--widget-chart-color-3": "chartColor3",
  "--widget-chart-color-4": "chartColor4",
  "--widget-chart-color-5": "chartColor5",
  "--widget-chart-color-6": "chartColor6",
  "--widget-chart-color-7": "chartColor7",
  "--widget-chart-color-8": "chartColor8",
  "--widget-chart-color-9": "chartColor9",
  "--widget-chart-color-10": "chartColor10",
  "--widget-border-radius": "borderRadius",
  "--widget-iframe-radius": "iframeCornerRadius",
  "--widget-border-width": "borderWidth",
  "--widget-border-style": "borderStyle",
  "--widget-border-color-light": "borderColorLight",
  "--widget-border-color-dark": "borderColorDark",
};

export const AGENT_DESCRIPTIONS: Record<string, string> = {
  Support:
    "Handles customer support questions, troubleshooting, and help requests using your knowledge base and documentation.",
  Copilot:
    "Executes actions and API calls on behalf of users — creating, updating, or retrieving data from connected services.",
  Analytics:
    "Analyzes data, runs reports, and answers questions about metrics by querying connected data sources directly.",
  Analyst:
    "Analyzes data, runs reports, and answers questions about metrics by querying connected data sources directly.",
  General:
    "Handles general conversation and questions that don't require specialized agent capabilities.",
};
